import { getPushNotificationsPublicKey, subscribePushNotifications, unsubscribePushNotifications } from '@api/pushNotifications'
import { reportUnexpectedClientError } from './client-error.service'


export type PushNotificationStatus = 'unsupported' | 'denied' | 'default' | 'disabled' | 'enabled'

export type PushNotificationPayload = {
    applicationID?: number
    title?: string
    body?: string
    icon?: string
    badge?: string
    url?: string
    tag?: string
    soundUrl?: string
}

export type PushNotificationState = {
    status: PushNotificationStatus
    isEnabled: boolean
    message: string
}

type PushNotificationActionResult = PushNotificationState & {
    isError?: boolean
}

const SERVICE_WORKER_PATH = '/crm-push-sw.js'
const FALLBACK_NOTIFICATION_DELAY_MS = 800
const SERVICE_WORKER_ACTIVATION_TIMEOUT_MS = 10000
const SUBSCRIPTION_SYNC_INTERVAL_MS = 60000
const DISABLED_BY_OPERATOR_KEY = 'crm_push_notifications_disabled'
const DEFAULT_NOTIFICATION_PATH = '/lk/applications'


class PushNotificationsService {
    private subscriptionSyncInFlight?: Promise<PushNotificationActionResult>
    private subscriptionSyncSession: string | null = null
    private subscriptionSyncPreferenceVersion = 0
    private preferenceVersion = 0
    private disableInFlight?: Promise<PushNotificationActionResult>
    private lastSuccessfulSyncAt = 0
    private lastSuccessfulSyncSession: string | null = null
    private notificationInFlight = new Map<string, Promise<void>>()

    /** Синхронизирует уже включённую подписку с сервером без показа prompt. */
    syncCurrentSubscription = async (): Promise<PushNotificationState> => {
        if (!this.isSupported()) return this.getUnsupportedState()

        if (this.isLocalOrigin()) {
            await this.unsubscribeCurrentBrowserSubscription()
            return this.getLocalOriginState()
        }

        if (Notification.permission === 'denied') return this.getDeniedState()
        if (Notification.permission !== 'granted') {
            return {
                status: 'default',
                isEnabled: false,
                message: 'Уведомления о новых заявках выключены.',
            }
        }
        if (this.isDisabledByOperator()) return this.getDisabledState()

        if (this.lastSuccessfulSyncAt > 0
            && this.lastSuccessfulSyncSession === this.getCurrentSession()
            && Date.now() - this.lastSuccessfulSyncAt < SUBSCRIPTION_SYNC_INTERVAL_MS) {
            return { status: 'enabled', isEnabled: true, message: 'Уведомления о новых заявках включены.' }
        }

        return this.createOrSyncSubscription()
    }

    /** Показывает OS-уведомление из открытой CRM, если service worker не показал своё. */
    showApplicationNotificationFallback = async (payload: PushNotificationPayload = {}) => {
        // Desktop notifications in an open CRM do not depend on PushManager.
        if (!this.canShowNotifications()) return
        if (this.isLocalOrigin()) return
        if (Notification.permission !== 'granted') return
        if (this.isDisabledByOperator()) return

        const tag = payload.tag || this.getApplicationNotificationTag(payload.applicationID)
        const pending = this.notificationInFlight.get(tag)
        if (pending) return pending

        const session = this.getCurrentSession()
        const preferenceVersion = this.preferenceVersion
        const canShow = () => Notification.permission === 'granted'
            && !this.isDisabledByOperator()
            && session === this.getCurrentSession()
            && preferenceVersion === this.preferenceVersion
        const notification = this.showApplicationNotification(payload, tag, canShow)
        this.notificationInFlight.set(tag, notification)
        try {
            await notification
        } finally {
            if (this.notificationInFlight.get(tag) === notification) this.notificationInFlight.delete(tag)
        }
    }

    private showApplicationNotification = async (payload: PushNotificationPayload, tag: string, canShow: () => boolean) => {
        const notificationPath = this.getSafeNotificationPath(
            payload.url,
            payload.applicationID ? `/lk/applications/${payload.applicationID}` : DEFAULT_NOTIFICATION_PATH,
        )

        await this.delay(FALLBACK_NOTIFICATION_DELAY_MS)
        if (!canShow()) return

        // An expired/missing push subscription must not disable the realtime fallback.
        let registration: ServiceWorkerRegistration | undefined
        if ('serviceWorker' in navigator) {
            try {
                registration = await this.getRegisteredServiceWorker(false)
            } catch (error) {
                reportUnexpectedClientError(error, 'PushNotifications.notificationWorker')
            }
        }
        if (!canShow()) return
        const existsNotifications = await this.getShownNotifications(registration, tag)
        if (!canShow()) return
        if (existsNotifications.length > 0) return

        const notificationOptions: NotificationOptions = {
            body: payload.body || 'Новая заявка в CRM.',
            icon: payload.icon || '/logo192.png',
            badge: payload.badge || '/logo192.png',
            data: {
                url: notificationPath,
                applicationID: payload.applicationID,
            },
            tag,
            renotify: true,
            silent: false,
        }

        if (registration?.showNotification) {
            try {
                await registration.showNotification(payload.title || 'ЦРМ Система', notificationOptions)
                return
            } catch (error) {
                // Desktop browsers can still display a Notification if worker delivery fails.
                reportUnexpectedClientError(error, 'PushNotifications.showWorkerNotification')
            }
        }

        if (!canShow()) return
        const notification = new Notification(payload.title || 'ЦРМ Система', notificationOptions)
        notification.onclick = (event) => {
            event.preventDefault()
            notification.close()
            window.focus()
            window.location.assign(notificationPath)
        }
    }

    /** Проверяет, может ли текущий браузер использовать Web Push. */
    getStatus = async (): Promise<PushNotificationState> => {
        if (!this.isSupported()) return this.getUnsupportedState()
        if (this.isLocalOrigin()) {
            await this.unsubscribeCurrentBrowserSubscription()
            return this.getLocalOriginState()
        }
        if (Notification.permission === 'denied') return this.getDeniedState()
        if (this.isDisabledByOperator()) return this.getDisabledState()

        if (Notification.permission === 'granted') return this.createOrSyncSubscription()

        return {
            status: 'default',
            isEnabled: false,
            message: 'Уведомления о новых заявках выключены.',
        }
    }

    /** Запрашивает разрешение, создаёт подписку браузера и сохраняет её на сервере. */
    enable = async (): Promise<PushNotificationActionResult> => {
        if (!this.isSupported()) return { ...this.getUnsupportedState(), isError: true }
        if (this.isLocalOrigin()) {
            await this.unsubscribeCurrentBrowserSubscription()
            return { ...this.getLocalOriginState(), isError: true }
        }
        if (Notification.permission === 'denied') return { ...this.getDeniedState(), isError: true }

        const preferenceVersion = ++this.preferenceVersion
        const permission = await Notification.requestPermission()
        if (preferenceVersion !== this.preferenceVersion) return this.getDisabledState()
        if (permission === 'denied') return { ...this.getDeniedState(), isError: true }
        if (permission !== 'granted') {
            return { status: 'default', isEnabled: false, isError: true, message: 'Разрешение на уведомления не выдано.' }
        }

        // A later enable waits for the preceding disable's final unsubscribe.
        if (this.disableInFlight) await this.disableInFlight
        if (preferenceVersion !== this.preferenceVersion) return this.getDisabledState()
        this.setDisabledByOperator(false)
        return this.createOrSyncSubscription()
    }

    /** Отключает подписку в браузере и помечает её неактивной на сервере. */
    disable = async (): Promise<PushNotificationActionResult> => {
        this.lastSuccessfulSyncAt = 0
        if (!this.isSupported()) return { ...this.getUnsupportedState(), isError: true }
        if (this.disableInFlight) return this.disableInFlight
        this.preferenceVersion += 1
        this.setDisabledByOperator(true)

        const pending = this.disableBrowserSubscription()
        this.disableInFlight = pending
        const clearPending = () => {
            if (this.disableInFlight === pending) this.disableInFlight = undefined
        }
        void pending.then(clearPending, clearPending)
        return pending
    }

    private disableBrowserSubscription = async (): Promise<PushNotificationActionResult> => {
        // subscribe()/the server POST may already be in flight. Finish them before
        // the final unsubscribe so their late completion cannot turn push back on.
        if (this.subscriptionSyncInFlight) await this.subscriptionSyncInFlight

        const registration = await navigator.serviceWorker.getRegistration()
        const subscription = await registration?.pushManager.getSubscription()

        if (!subscription) {
            return { status: 'disabled', isEnabled: false, message: 'Уведомления о новых заявках уже выключены.' }
        }

        const endpoint = subscription.endpoint
        await subscription.unsubscribe()

        const unsubscribeResponse = await unsubscribePushNotifications(endpoint)
        if (unsubscribeResponse.status !== 200) {
            return { status: 'disabled', isEnabled: false, isError: true, message: unsubscribeResponse.message || 'Подписка отключена в браузере, но сервер не подтвердил отключение.' }
        }

        return { status: 'disabled', isEnabled: false, message: 'Уведомления о новых заявках выключены.' }
    }

    private canShowNotifications = () => typeof window !== 'undefined'
        && window.isSecureContext
        && 'Notification' in window

    private isSupported = () => this.canShowNotifications()
        && 'serviceWorker' in navigator
        && 'PushManager' in window

    private isLocalOrigin = () => ['localhost', '127.0.0.1'].includes(window.location.hostname)

    private getCurrentSession = () => {
        try { return localStorage.getItem('us_id') }
        catch { return null }
    }

    private isDisabledByOperator = () => {
        try { return localStorage.getItem(DISABLED_BY_OPERATOR_KEY) === '1' }
        catch { return false }
    }

    private setDisabledByOperator = (disabled: boolean) => {
        try {
            if (disabled) localStorage.setItem(DISABLED_BY_OPERATOR_KEY, '1')
            else localStorage.removeItem(DISABLED_BY_OPERATOR_KEY)
        } catch {
            // Storage may be unavailable in privacy mode; browser permission still applies.
        }
    }

    private getDisabledState = (): PushNotificationState => ({
        status: 'disabled', isEnabled: false, message: 'Уведомления о новых заявках выключены.',
    })

    private getSafeNotificationPath = (value: string | undefined, fallbackPath: string) => {
        try {
            const targetUrl = new URL(value || fallbackPath, window.location.origin)
            if (targetUrl.origin !== window.location.origin) return fallbackPath

            return `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`
        } catch {
            return fallbackPath
        }
    }

    private unsubscribeCurrentBrowserSubscription = async () => {
        try {
            const registration = await navigator.serviceWorker.getRegistration()
            const subscription = await registration?.pushManager.getSubscription()

            if (!subscription) return

            const endpoint = subscription.endpoint
            await subscription.unsubscribe()
            await unsubscribePushNotifications(endpoint)
        } catch (error) {
            reportUnexpectedClientError(error, 'PushNotifications.unsubscribeCurrentBrowserSubscription')
            return
        }
    }

    private syncSubscription = async (subscription: PushSubscription) => {
        try {
            await subscribePushNotifications(subscription.toJSON(), window.location.origin)
        } catch (error) {
            reportUnexpectedClientError(error, 'PushNotifications.syncSubscription')
            return
        }
    }

    private createOrSyncSubscription = (): Promise<PushNotificationActionResult> => {
        const session = this.getCurrentSession()
        const preferenceVersion = this.preferenceVersion
        if (this.subscriptionSyncInFlight) {
            if (this.subscriptionSyncSession === session && this.subscriptionSyncPreferenceVersion === preferenceVersion) {
                return this.subscriptionSyncInFlight
            }
            return this.subscriptionSyncInFlight.then(() => this.createOrSyncSubscription())
        }
        const pending = this.syncBrowserSubscription(session, preferenceVersion)
        this.subscriptionSyncSession = session
        this.subscriptionSyncPreferenceVersion = preferenceVersion
        this.subscriptionSyncInFlight = pending
        const clearPending = () => {
            if (this.subscriptionSyncInFlight === pending) this.subscriptionSyncInFlight = undefined
        }
        void pending.then(clearPending, clearPending)
        return pending
    }

    private syncBrowserSubscription = async (session: string | null, preferenceVersion: number): Promise<PushNotificationActionResult> => {
        const canSync = () => !this.isDisabledByOperator()
            && session === this.getCurrentSession()
            && preferenceVersion === this.preferenceVersion
        try {
            if (!canSync()) return this.getDisabledState()
            const publicKeyResponse = await getPushNotificationsPublicKey()
            if (!canSync()) return this.getDisabledState()
            if (publicKeyResponse.status !== 200 || !publicKeyResponse.data?.publicKey) {
                return { status: 'disabled', isEnabled: false, isError: true, message: publicKeyResponse.message || 'Не удалось получить ключ уведомлений.' }
            }

            const registration = await this.getRegisteredServiceWorker()
            if (!canSync()) return this.getDisabledState()
            const subscription = await this.getOrCreateSubscription(registration, publicKeyResponse.data.publicKey, canSync)
            if (!subscription || !canSync()) return this.getDisabledState()
            const subscribeResponse = await subscribePushNotifications(subscription.toJSON(), window.location.origin)
            if (!canSync()) return this.getDisabledState()

            if (subscribeResponse.status !== 200) {
                return { status: 'disabled', isEnabled: false, isError: true, message: subscribeResponse.message || 'Не удалось сохранить подписку уведомлений.' }
            }

            this.lastSuccessfulSyncAt = Date.now()
            this.lastSuccessfulSyncSession = session
            return { status: 'enabled', isEnabled: true, message: 'Уведомления о новых заявках включены.' }
        } catch (error) {
            const isExpectedPermissionFailure = error instanceof DOMException
                && ['AbortError', 'NotAllowedError'].includes(error.name)
            if (!isExpectedPermissionFailure) {
                reportUnexpectedClientError(error, 'PushNotifications.createOrSyncSubscription')
            }
            return { status: 'disabled', isEnabled: false, isError: true, message: 'Не удалось включить уведомления о новых заявках.' }
        }
    }

    private getUnsupportedState = (): PushNotificationState => ({
        status: 'unsupported',
        isEnabled: false,
        message: 'Браузер или текущее подключение не поддерживает Web Push. Нужен HTTPS или localhost.',
    })

    private getLocalOriginState = (): PushNotificationState => ({
        status: 'unsupported',
        isEnabled: false,
        message: 'Push-уведомления включаются только с рабочего адреса CRM.',
    })

    private getDeniedState = (): PushNotificationState => ({
        status: 'denied',
        isEnabled: false,
        message: 'Уведомления заблокированы в настройках сайта браузера.',
    })

    private registerServiceWorker = async () => navigator.serviceWorker.register(SERVICE_WORKER_PATH)

    private getRegisteredServiceWorker = async (update = true) => {
        const existsRegistration = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_PATH)
        const registration = existsRegistration || await this.registerServiceWorker()
        if (update) await this.updateServiceWorkerRegistration(registration)
        await this.waitForActiveServiceWorker(registration)
        return registration
    }

    private updateServiceWorkerRegistration = async (registration: ServiceWorkerRegistration) => {
        try {
            // Chromium resolves update() with undefined; keep the registration itself.
            await registration.update()
        } catch {
            // A cached active worker remains usable while the network is unavailable.
        }
        return registration
    }

    private waitForActiveServiceWorker = async (registration: ServiceWorkerRegistration) => {
        if (registration.active?.state === 'activated') return
        const worker = registration.installing || registration.waiting || registration.active
        if (!worker) throw new Error('Notification service worker is not active.')

        await new Promise<void>((resolve, reject) => {
            const finish = (error?: Error) => {
                clearTimeout(timeout)
                worker.removeEventListener('statechange', onStateChange)
                if (error) reject(error)
                else resolve()
            }
            const onStateChange = () => {
                if (worker.state === 'activated') finish()
                else if (worker.state === 'redundant') finish(new Error('Notification service worker activation failed.'))
            }
            const timeout = setTimeout(() => finish(new Error('Notification service worker activation timed out.')), SERVICE_WORKER_ACTIVATION_TIMEOUT_MS)
            worker.addEventListener('statechange', onStateChange)
            onStateChange()
        })
    }

    private getApplicationNotificationTag = (applicationID?: number) => `crm-application-${applicationID || Date.now()}`

    private getShownNotifications = async (registration: ServiceWorkerRegistration | undefined, tag: string) => {
        try {
            if (!registration?.getNotifications) return []
            return await registration.getNotifications({ tag })
        } catch {
            return []
        }
    }

    private delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

    private getOrCreateSubscription = async (registration: ServiceWorkerRegistration, publicKey: string, canSync: () => boolean) => {
        const existsSubscription = await registration.pushManager.getSubscription()
        if (!canSync()) return
        const applicationServerKey = this.urlBase64ToUint8Array(publicKey)
        if (existsSubscription) {
            const existingKey = existsSubscription.options?.applicationServerKey
            const hasMatchingKey = !existingKey || this.haveSameBytes(new Uint8Array(existingKey), applicationServerKey)
            const hasExpired = existsSubscription.expirationTime !== null
                && existsSubscription.expirationTime !== undefined
                && existsSubscription.expirationTime <= Date.now()
            if (hasMatchingKey && !hasExpired) return existsSubscription

            if (!await existsSubscription.unsubscribe()) throw new Error('Could not replace expired notification subscription.')
            await unsubscribePushNotifications(existsSubscription.endpoint)
        }

        if (!canSync()) return
        return registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey,
        })
    }

    private haveSameBytes = (left: Uint8Array, right: Uint8Array) => left.length === right.length
        && left.every((value, index) => value === right[index])

    private urlBase64ToUint8Array = (base64String: string) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4)
        const base64 = `${base64String}${padding}`.replace(/-/g, '+').replace(/_/g, '/')
        const rawData = window.atob(base64)
        const outputArray = new Uint8Array(rawData.length)

        for (let index = 0; index < rawData.length; index += 1) {
            outputArray[index] = rawData.charCodeAt(index)
        }

        return outputArray
    }
}

const pushNotificationsService = new PushNotificationsService()

export default pushNotificationsService
